import { isDistributorAdmin } from '../utils/roles';
import axios from 'axios';
import { PRODUCTION_API_URL } from '../config/backend';
import { markSessionEnded } from '../utils/sessionEnded';
import {
  captureSession,
  isSessionCurrent,
  assertCurrentSession,
  clearCredentials,
} from '../utils/authSession';

/**
 * THE API BASE.
 *
 * REACT_APP_API_URL still decides it, because that is where the deployment platforms configure it
 * and both are set correctly. What changed on 8/25/26 is the FALLBACK, which used to be
 * 'http://localhost:3001/api'.
 *
 * That default is fine for a developer and indefensible in a production build. `.env` is
 * gitignored, so a fresh clone has no REACT_APP_API_URL at all: `npm run build` there emits a
 * bundle that asks the visitor's own machine for the API. It compiles, it deploys, it renders the
 * login screen, and every request fails as "Network error" - a browser will not say which host it
 * could not reach or why. Only the hosting dashboards stood between that and production, and
 * nothing in the repo recorded it.
 *
 * So a production build with nothing configured now falls back to the real backend, from
 * config/backend.js - where setupProxy.js already kept it for development and where the mobile app
 * keeps its own copy.
 *
 * A RELATIVE value is treated as unconfigured in production, and that is not pedantry: '/api' is
 * the documented development value, and Cloudflare Pages answers '/api/*' with the SPA fallback -
 * so a relative base returns index.html with a 200 and hands JSON parsing a web page. It is the
 * one wrong answer that looks like a working one.
 */
const configuredApiUrl = (process.env.REACT_APP_API_URL || '').trim();
const isAbsoluteUrl = /^https?:\/\//i.test(configuredApiUrl);
const BASE_URL =
  process.env.NODE_ENV === 'production'
    ? isAbsoluteUrl
      ? configuredApiUrl
      : PRODUCTION_API_URL
    : configuredApiUrl || 'http://localhost:3001/api';

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 60000,
  headers: { 'Content-Type': 'application/json' },
});

let refreshInFlight = null;

/**
 * HAS THIS ACCESS TOKEN ALREADY EXPIRED?
 *
 * Read locally, from the token's own `exp`, so a request that is CERTAIN to be rejected is never
 * sent. That is not an optimisation - it is the difference between a working page and a broken one:
 *
 *   ThrottlerGuard is registered as an APP_GUARD (app.module.ts) and global guards run before the
 *   controller's JwtAuthGuard, so the rate limiter counts a request BEFORE anything checks who sent
 *   it - `storageService.increment` is called unconditionally, ahead of the isBlocked test. An
 *   expired token therefore spends a hit, gets a 401, and the retry below spends a second. On the
 *   statement scan routes, whose limit is deliberately ONE request a minute because each one walks
 *   the entire QuickBooks company file, that is a guaranteed 429: access tokens last 15 minutes, so
 *   opening the page after a coffee break reliably produced "this was checked less than a minute
 *   ago" and stayed that way for a full minute, on a read the operator had not yet made once.
 *
 * The limiter counting unauthenticated requests is correct and must stay that way - it is what
 * stops an anonymous caller starving the weekly run. The client's job is not to send them.
 *
 * `skewMs` refreshes a token that is about to expire in flight. Anything unreadable returns false,
 * so a malformed or opaque token behaves exactly as it did before: send it, let the server decide.
 */
export function isTokenExpired(token, skewMs = 10000, now = Date.now()) {
  try {
    const payload = String(token).split('.')[1];
    if (!payload) return false;
    // base64url, and UTF-8 safe: a display name with an accent in the payload would otherwise throw
    // and make every token look valid, quietly disabling all of this for that user.
    const bytes = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const json = JSON.parse(
      decodeURIComponent(
        bytes
          .split('')
          .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join('')
      )
    );
    if (typeof json?.exp !== 'number') return false;
    return json.exp * 1000 - skewMs <= now;
  } catch {
    return false;
  }
}

/**
 * Exchange the refresh token for a new access token, once, however many callers are waiting.
 *
 * Shared by both interceptors below so there is one refresh in flight and one queue, rather than
 * each path keeping its own idea of whether a refresh is happening.
 */
async function refreshAccessToken(session) {
  assertCurrentSession(session);
  if (refreshInFlight && isSessionCurrent(refreshInFlight.session)) return refreshInFlight.promise;
  const flight = { session };
  flight.promise = (async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) throw new Error('No refresh token');

    // Bare axios, not `api`: going back through this client would re-enter the interceptors.
    let data;
    try {
      ({ data } = await axios.post(
        `${BASE_URL}/auth/refresh-token`,
        { refreshToken },
        { timeout: 20000 }
      ));
    } catch (error) {
      if (isSessionCurrent(session) && localStorage.getItem('refreshToken') !== refreshToken) {
        throw supersededRefresh();
      }
      throw error;
    }
    assertCurrentSession(session);
    if (localStorage.getItem('refreshToken') !== refreshToken) throw supersededRefresh();
    const { accessToken, refreshToken: newRefreshToken } = data.data;
    if (
      typeof accessToken !== 'string' ||
      !accessToken ||
      typeof newRefreshToken !== 'string' ||
      !newRefreshToken
    ) {
      throw new Error('We could not refresh your session. Please sign in again.');
    }
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', newRefreshToken);
    return accessToken;
  })();
  refreshInFlight = flight;
  try {
    return await flight.promise;
  } finally {
    if (refreshInFlight === flight) refreshInFlight = null;
  }
}

/**
 * The session is over. Clear it and get out of the app rather than retrying into a wall.
 *
 * The reason is left in sessionStorage for the login page to show - a hard redirect with no
 * explanation looked like a crash, and "the app just logged me out" is a support call.
 */
function supersededRefresh() {
  const error = new Error('Your session was updated. Please try again.');
  error.sessionSuperseded = true;
  return error;
}

function endSession(session, error) {
  // Another tab may have rotated this account's credentials while our refresh was pending.
  // Its newer pair must neither be overwritten nor cleared by this older operation.
  if (error?.sessionSuperseded || !isSessionCurrent(session)) return;
  let returnTo = '/login';
  try {
    const account = JSON.parse(localStorage.getItem('user'));
    if (account?.role === 'master_distributor' || isDistributorAdmin(account))
      returnTo = '/distributor/login';
  } catch {
    /* A damaged cached profile must not block logout. */
  }
  markSessionEnded('expired');
  clearCredentials();
  window.location.href = returnTo;
}

/**
 * The two requests that must NEVER wait on a refresh.
 *
 * Signing in cannot be made to depend on a refresh token succeeding - a stale expired session left
 * in localStorage would otherwise hijack the login request, fail, and bounce the user back to the
 * login page they were already on. Refreshing obviously cannot refresh itself.
 */
const NO_PREFLIGHT_REFRESH = ['/auth/login', '/auth/distributor/login', '/auth/refresh-token'];

/**
 * The same two requests must never be REPAIRED with a refresh either. A 401 from /auth/login is
 * a wrong password, not a stale session: routing it into the refresh path failed (no refresh
 * token), ended a session that did not exist, and hard-reloaded the login page - which is how a
 * typo in a password became a blank form with the typed email gone and no message.
 */
const isSessionEntry = (config) =>
  NO_PREFLIGHT_REFRESH.some((p) => String(config?.url || '').startsWith(p));

/** What the backend's ThrottlerException reads like once it reaches a person. */
export const TOO_MANY_REQUESTS_MESSAGE =
  'Too many requests in a short time. Please wait a minute and try again.';

api.interceptors.request.use(
  async (config) => {
    const session = config.sessionBinding || captureSession();
    config.sessionBinding = session;
    assertCurrentSession(session);
    let token = localStorage.getItem('accessToken');
    const url = String(config.url || '');

    if (token && !NO_PREFLIGHT_REFRESH.some((p) => url.startsWith(p)) && isTokenExpired(token)) {
      try {
        token = await refreshAccessToken(session);
      } catch (e) {
        // REJECTED, not sent. Letting the doomed request through is what spends the rate limit.
        endSession(session, e);
        return Promise.reject(e);
      }
    }

    assertCurrentSession(session);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // The exception filter answers a 429 with `error: "ThrottlerException: Too Many Requests"`,
    // and several pages print `error` verbatim. Rewritten here, once, so every page benefits.
    if (error.response?.status === 429) {
      const data = error.response.data;
      error.response.data =
        data && typeof data === 'object' && !Array.isArray(data) ? data : { raw: data };
      error.response.data.error = TOO_MANY_REQUESTS_MESSAGE;
      error.response.data.message = TOO_MANY_REQUESTS_MESSAGE;
      return Promise.reject(error);
    }

    // STILL HERE, and still necessary. The preflight above removes the PREDICTABLE 401 - a token
    // whose own expiry has passed - which is the one that was costing a rate-limit hit on every
    // route. It cannot remove the unpredictable ones: a session revoked server-side, a key
    // rotation, a clock further out than the skew allowance. Those land here, once.
    // An acceptance save is never replayed, even after a 401. The gate reconciles it with GET.
    const neverReplay =
      originalRequest?.skipAuthReplay ||
      String(originalRequest?.url || '').split('?')[0] === '/legal/acceptance';
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !neverReplay &&
      !originalRequest._retry &&
      !isSessionEntry(originalRequest)
    ) {
      // Set BEFORE awaiting, and for queued callers too - previously a request that arrived while a
      // refresh was already in flight was retried without ever being marked, so a second 401 sent
      // it round again.
      originalRequest._retry = true;
      try {
        const accessToken = await refreshAccessToken(originalRequest.sessionBinding);
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        endSession(originalRequest.sessionBinding, refreshError);
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
